import React, { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useBlogCategory } from "../useHook";
import Swal from "sweetalert2";
import AddBlogCategoryModal from "../../../Components/Modals/BlogVategoryModal/BlogCategoryModal";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import Loader from "../../../Utils/loader";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";

function BlogCategories() {
  const [modalOpen, setModalOpen] = useState(false);
  const [blogCategories, setBlogCategories] = useState([]);
  const [currentBlogCategory, setCurrentBlogCategory] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [nameQuery, setNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedCategoryForPosition, setSelectedCategoryForPosition] =
    useState(null);
  const {
    addBlogCategories,
    getBlogCategories,
    updateBlogCategory,
    updateCategoryStatus,
    searchBlogCategories,
    updateBlogCategoryPosition,
  } = useBlogCategory();

  const navigate = useNavigate();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  useEffect(() => {
    const fetchBlogCategories = async () => {
      setIsLoading(true);
      try {
        await getBlogCategories(
          setBlogCategories,
          setTotal,
          offset,
          limit,
          nameQuery,
          statusFilter,
        );
      } catch (error) {
        console.error("Error fetching blog categories:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load blog categories. Please refresh the page.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogCategories();
  }, [offset]);

  const handleSaveCategory = async (categoryData, id = null) => {
    try {
      let response;
      if (id) {
        response = await updateBlogCategory(id, categoryData);
      } else {
        response = await addBlogCategories(categoryData);
      }

      if (response) {
        await getBlogCategories(
          setBlogCategories,
          setTotal,
          offset,
          limit,
          nameQuery,
          statusFilter,
        );
        toggleModal();
        Swal.fire("Success", "Category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    }
  };

  const handleEditCategory = (category) => {
    setCurrentBlogCategory(category);
    toggleModal();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await updateCategoryStatus(id, newStatus);

      if (response) {
        setBlogCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === id ? { ...category, status: newStatus } : category,
          ),
        );

        Swal.fire({
          title: "Success",
          text: `Category status has been ${
            newStatus ? "activated" : "deactivated"
          } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update category status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleViewPage = (id) => {
    navigate("/blog-category/view", { state: { id } });
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchBlogCategories(
        nameQuery,
        statusFilter,
        0,
        limit,
      );
      setBlogCategories(response.data);
      const totalRecords =
        response.pagination?.totalCount || response.data.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNameQuery("");
    setStatusFilter("");
    setOffset(0);
    getBlogCategories(setBlogCategories, setTotal, 0, limit, "", "");
  };

  const handlePositionClick = (category) => {
    setSelectedCategoryForPosition(category);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (categoryId, newPosition) => {
    await updateBlogCategoryPosition(categoryId, newPosition);
    await getBlogCategories(
      setBlogCategories,
      setTotal,
      offset,
      limit,
      nameQuery,
      statusFilter,
    );
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedCategoryForPosition(null);
  };

  if (isLoading) {
    return <Loader message="Loading Blog Categories..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Blog Categories</h2>

        <button
          onClick={() => {
            setCurrentBlogCategory(null);
            toggleModal();
          }}
          className="btn btn-primary h-[44px]"
        >
          <FiPlus className="text-white" />
          Add Blog Category
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Category Name</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(blogCategories) && blogCategories?.length > 0 ? (
                blogCategories?.map((category, index) => (
                  <tr
                    key={category.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <button
                        type="button"
                        onClick={() => handlePositionClick(category)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      >
                        {category.position ?? offset + index + 1}
                      </button>
                    </td>
                    <td className="table-td">{category.name}</td>
                    <td className="table-td">
                      <div
                        className={`status-toggle ${
                          category.status ? "active" : "bg-[#cccccc]"
                        }`}
                        onClick={() =>
                          handleToggleStatus(category.id, category.status)
                        }
                      >
                        <div className="status-toggle-handle"></div>
                      </div>
                    </td>
                    <td className="table-td flex space-x-4">
                      <button
                        className="btn btn-edit text-white"
                        onClick={() => handleEditCategory(category)}
                      >
                        <FiEdit2 className="text-white" /> Edit
                      </button>
                      <button
                        className="btn btn-view text-white"
                        onClick={() => handleViewPage(category.id)}
                      >
                        <IoEye className="text-white" /> View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500">
                    No blog categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Component */}
      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </div>
      )}

      <AddBlogCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentBlogCategory}
        handleSaveCategory={handleSaveCategory}
      />

      {isPositionModalOpen && selectedCategoryForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={
            selectedCategoryForPosition.position ??
            offset +
              blogCategories.findIndex(
                (c) => c.id === selectedCategoryForPosition.id,
              ) +
              1
          }
          recordId={selectedCategoryForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Blog Category"
        />
      )}
    </div>
  );
}

export default BlogCategories;
